import React, { useState } from 'react';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';

/* ── Planet metadata ── */
const PLANET_META = {
  Sun:     { sa: 'Surya',   sym: '☉' },
  Moon:    { sa: 'Chandra', sym: '☽' },
  Mars:    { sa: 'Mangal',  sym: '♂' },
  Mercury: { sa: 'Budh',    sym: '☿' },
  Jupiter: { sa: 'Guru',    sym: '♃' },
  Venus:   { sa: 'Shukra',  sym: '♀' },
  Saturn:  { sa: 'Shani',   sym: '♄' },
  Rahu:    { sa: 'Rahu',    sym: '☊' },
  Ketu:    { sa: 'Ketu',    sym: '☋' },
};

const PLANET_ORDER = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

const SIGN_SYMBOLS = {
  Mesh:'♈', Vrishabh:'♉', Mithun:'♊', Kark:'♋',
  Simha:'♌', Kanya:'♍', Tula:'♎', Vrishchik:'♏',
  Dhanu:'♐', Makar:'♑', Kumbh:'♒', Meen:'♓',
};

function SignBadge({ label, sign, testId }) {
  if (!sign) return null;
  const symbol = SIGN_SYMBOLS[sign.sa] || sign.symbol || '✦';
  return (
    <div className="text-center" data-testid={testId} style={{ flex: 1 }}>
      <div style={{ fontSize: 22, marginBottom: 4, color: '#C9A84C' }}>{symbol}</div>
      <div style={{ fontSize: 10, color: '#8A8580', textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#F5F0E8' }}>{sign.sa}</div>
      <div style={{ fontSize: 11, color: '#8A8580' }}>{sign.en}</div>
    </div>
  );
}

/* ── Planet row ── */
function PlanetRow({ planet, isOdd }) {
  const meta = PLANET_META[planet.name] || { sa: planet.name, sym: '●' };
  const sign = planet.sign || {};
  const signSym = SIGN_SYMBOLS[sign.sa] || sign.symbol || '';

  return (
    <div
      data-testid={`planet-row-${planet.name.toLowerCase()}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr 1fr 36px',
        alignItems: 'center',
        gap: 6,
        padding: '9px 12px',
        background: isOdd ? 'rgba(255,255,255,0.02)' : 'transparent',
        borderRadius: 8,
      }}
    >
      {/* Symbol */}
      <div style={{ fontSize: 16, color: '#C9A84C', textAlign: 'center', lineHeight: 1 }}>
        {meta.sym}
      </div>

      {/* Planet name */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8', lineHeight: 1.2 }}>
          {meta.sa}
        </div>
        <div style={{ fontSize: 11, color: '#8A8580' }}>{planet.name}</div>
      </div>

      {/* Sign */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 13, color: '#C9A84C' }}>{signSym}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8' }}>{sign.sa || '—'}</span>
        </div>
        <div style={{ fontSize: 11, color: '#8A8580' }}>{sign.en || ''}</div>
      </div>

      {/* House */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#C9A84C' }}>
          {planet.house || '—'}
        </div>
        <div style={{ fontSize: 10, color: '#8A8580' }}>H</div>
      </div>
    </div>
  );
}

/* ── Main card ── */
export default function ZodiacProfileCard({ profile, simplified = false }) {
  const { lang } = useLang();
  const tr = t[lang];
  const chart = profile?.chart;

  const [expanded, setExpanded] = useState(false);

  /* Sort planets in canonical order */
  const planets = PLANET_ORDER
    .map(name => chart?.planets?.find(p => p.name === name))
    .filter(Boolean);

  return (
    <div
      data-testid="zodiac-profile-card"
      className="card"
      style={{
        background: `linear-gradient(135deg, rgba(17,24,39,0.97) 0%, rgba(11,16,30,0.97) 100%)`,
        border: '1px solid rgba(201,168,76,0.35)',
        position: 'relative',
        overflow: 'hidden',
        padding: 0,
      }}
    >
      {/* Gold accent top border */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }} />

      <div style={{ padding: '20px 20px 16px' }}>
        {/* Name */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <p style={{ fontSize: 11, color: '#C9A84C', letterSpacing: '0.12em',
            textTransform: 'uppercase', marginBottom: 4 }}>
            {tr.yourChart}
          </p>
          <p style={{ fontSize: 17, fontFamily: "'Playfair Display', serif",
            fontWeight: 700, color: '#F5F0E8' }}>
            {profile?.name || '—'}
          </p>
        </div>

        {/* Big three badges */}
        {chart ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <SignBadge label={tr.sunSign}   sign={chart.sunSign}   testId="sun-sign-badge" />
            <div style={{ width: 1, background: 'rgba(201,168,76,0.15)', margin: '0 4px' }} />
            <SignBadge label={tr.moonSign}  sign={chart.moonSign}  testId="moon-sign-badge" />
            <div style={{ width: 1, background: 'rgba(201,168,76,0.15)', margin: '0 4px' }} />
            <SignBadge label={tr.ascendant} sign={chart.ascendant} testId="ascendant-badge" />
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#8A8580', fontSize: 13 }}>
            Chart not yet generated
          </p>
        )}
      </div>

      {/* Expand/collapse toggle */}
      {!simplified && chart && planets.length > 0 && (
        <>
          <button
            data-testid="toggle-planets-btn"
            onClick={() => setExpanded(e => !e)}
            style={{
              width: '100%',
              background: 'rgba(201,168,76,0.06)',
              border: 'none',
              borderTop: '1px solid rgba(201,168,76,0.12)',
              padding: '10px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              color: '#C9A84C',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              fontFamily: "'Inter', sans-serif",
              transition: 'background 0.2s',
            }}
          >
            <span style={{
              display: 'inline-block',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.25s ease',
              fontSize: 10,
            }}>▾</span>
            {expanded ? tr.hidePlanets : tr.viewPlanets}
          </button>

          {/* Planet table */}
          <div
            data-testid="planet-table"
            style={{
              maxHeight: expanded ? `${planets.length * 56 + 48}px` : '0px',
              overflow: 'hidden',
              transition: 'max-height 0.35s ease',
            }}
          >
            {/* Header row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr 1fr 36px',
              gap: 6,
              padding: '10px 12px 6px',
              borderTop: '1px solid rgba(201,168,76,0.08)',
            }}>
              {['', tr.planet, tr.sign, tr.house].map((h, i) => (
                <div key={i} style={{
                  fontSize: 10, fontWeight: 600, color: '#8A8580',
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  textAlign: i === 3 ? 'center' : 'left',
                }}>
                  {h}
                </div>
              ))}
            </div>

            {/* Planet rows */}
            <div style={{ padding: '0 8px 12px' }}>
              {planets.map((p, i) => (
                <PlanetRow key={p.name} planet={p} isOdd={i % 2 !== 0} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
